/**
 * Ошибки API на языке интерфейса — по коду (ADR-0019).
 *
 * Сервер отвечает конвертом `{error: {code, message, details}}`: `code` —
 * стабильный контракт (реестр `backend/serving/error_codes.py`), `message` —
 * русский текст для отладки и для языка-источника. Перевод делается ЗДЕСЬ, по
 * коду: так ответ сервера не зависит от языка, кэш ответов не делится по
 * языкам, а текст ошибки переводится тем же процессом, что и весь интерфейс.
 *
 * На русском показывается текст сервера — он конкретнее общего («Контур не
 * найден: 123»). На остальных языках — строка каталога по коду; кода нет в
 * каталоге — общая строка с кодом, чтобы поддержка могла найти причину.
 */
import type { IntlShape } from 'react-intl';
import { defineMessages } from './define';
import { SOURCE_LOCALE } from './locales';

export const API_ERROR_MESSAGES = defineMessages({
  invalid_credentials: { id: 'errors.invalid_credentials', defaultMessage: 'Неверный логин или пароль.' },
  not_authenticated: { id: 'errors.not_authenticated', defaultMessage: 'Нужно войти: без входа это действие недоступно.' },
  outside_scope: { id: 'errors.outside_scope', defaultMessage: 'Это вне области вашего аккаунта.' },
  role_required: { id: 'errors.role_required', defaultMessage: 'Аккаунту не назначена роль. Роль назначает администратор района.' },
  not_found: { id: 'errors.not_found', defaultMessage: 'Не найдено.' },
  validation_error: { id: 'errors.validation_error', defaultMessage: 'Параметры запроса не прошли проверку.' },
  rate_limited: { id: 'errors.rate_limited', defaultMessage: 'Слишком много запросов подряд. Повторите чуть позже.' },
  internal_error: { id: 'errors.internal_error', defaultMessage: 'Внутренняя ошибка сервера.' },
  network_error: { id: 'errors.network_error', defaultMessage: 'Сервер не ответил: проверьте связь.' },
  csrf_failed: {
    id: 'errors.csrf_failed',
    defaultMessage: 'Запрос отклонён проверкой безопасности. Обновите страницу и повторите.',
    description: 'Сервер отклонил запись: не прошла проверка CSRF (страница устарела или открыта с чужого адреса)',
  },
  http_error: {
    id: 'errors.http_error',
    defaultMessage: 'Сервер отклонил запрос.',
    description: 'Общий отказ сервера на уровне протокола, без более точного кода',
  },
  aborted: {
    id: 'errors.aborted',
    defaultMessage: 'Запрос отменён.',
    description: 'Клиент сам прервал запрос, например при уходе с экрана или смене фильтра',
  },
  report_refused: {
    id: 'errors.report_refused',
    defaultMessage: 'Отчёт не принят: данные не прошли проверку.',
    description: 'Приём отчёта по работе (ввод или импорт) отклонён проверкой стыка отчёта и работы плана',
  },
  ambiguous_scope: {
    id: 'errors.ambiguous_scope',
    defaultMessage: 'Выберите одну область: район, массив или хозяйство.',
    description: 'Запрос баланса передал одновременно массив и хозяйство; итог считается по одной области',
  },
  invalid_bbox: {
    id: 'errors.invalid_bbox',
    defaultMessage: 'Границы карты заданы неверно.',
    description: 'Карта запросила контуры по прямоугольнику с неверными координатами',
  },
  malformed_cycle: {
    id: 'errors.malformed_cycle',
    defaultMessage: 'Агроцикл указан в неверном формате.',
    description: 'В запросе параметр cycle не в виде «сезон:культура», например 2026:COTTON',
  },
  unknown_cycle: {
    id: 'errors.unknown_cycle',
    defaultMessage: 'Такого агроцикла нет.',
    description: 'В запросе указан агроцикл (сезон и культура), которого нет в данных',
  },
  conflicting_cycle: {
    id: 'errors.conflicting_cycle',
    defaultMessage: 'Параметры агроцикла противоречат друг другу.',
    description: 'Запрос передал агроцикл двумя способами, и они указывают на разные циклы',
  },
  cycle_required: {
    id: 'errors.cycle_required',
    defaultMessage: 'Нужно указать агроцикл в виде «сезон:культура».',
    description: 'Экран воды или кабинета открыт без параметра cycle, а ему нужен агроцикл',
  },
  unknown_stage: {
    id: 'errors.unknown_stage',
    defaultMessage: 'Такого этапа нет в справочнике этапов сезона.',
    description: 'Запрос по этапу агроцикла с этапом, которого нет в справочнике; ответ по всему циклу подменил бы этап',
  },
  daily_needs_campaign: {
    id: 'errors.daily_needs_campaign',
    defaultMessage: 'Дневной ряд считается для одной кампании: укажите кампанию и сводку.',
    description: 'Запрошен дневной ряд без campaign_id или без признака сводки',
  },
  malformed_situation_key: {
    id: 'errors.malformed_situation_key',
    defaultMessage: 'Ключ ситуации задан в неверном формате.',
    description: 'Открытие или действие по ситуации с ключом, который не удаётся разобрать',
  },
  not_irrigation: {
    id: 'errors.not_irrigation',
    defaultMessage: 'Это не полив: событие полива закрывает только работа-полив.',
    description: 'Событие полива привязывают к работе, категория которой не полив',
  },
  adapter_unknown: {
    id: 'errors.adapter_unknown',
    defaultMessage: 'Источник данных размещения не найден.',
    description: 'Импорт размещения выбран через адаптер, которого нет в списке поддерживаемых',
  },
  crop_not_found: {
    id: 'errors.crop_not_found',
    defaultMessage: 'Культура не найдена в справочнике.',
    description: 'При импорте или создании размещения указана культура, которой нет в справочнике',
  },
  invalid_if_match: {
    id: 'errors.invalid_if_match',
    defaultMessage: 'Ревизия шаблона в заголовке If-Match должна быть числом.',
    description: 'Клиент отправил запись шаблона с неверным значением ревизии',
  },
  already_cancelled: {
    id: 'errors.already_cancelled',
    defaultMessage: 'Работа плана уже отменена.',
    description: 'Менеджер отменяет работу плана, которая уже отменена',
  },
  already_launched: {
    id: 'errors.already_launched',
    defaultMessage: 'Кампания по этой работе плана уже запущена.',
    description: 'Менеджер нажал «Запустить кампанию» у работы плана, которая уже запущена',
  },
  bad_transition: {
    id: 'errors.bad_transition',
    defaultMessage: 'Из текущего состояния кампании так перейти нельзя: приостановить можно идущую, возобновить можно приостановленную.',
    description: 'Менеджер приостанавливает или возобновляет кампанию в неподходящем для этого состоянии',
  },
  campaign_closed: {
    id: 'errors.campaign_closed',
    defaultMessage: 'Кампания закрыта: решения по ней больше не принимаются.',
    description: 'Менеджер пытается изменить охват или решение по кампании, которая завершена или отменена',
  },
  cycle_finished: {
    id: 'errors.cycle_finished',
    defaultMessage: 'Агроцикл завершён: охват кампании расширять уже нельзя.',
    description: 'Менеджер распространяет кампанию на новые поля, когда агроцикл не идёт',
  },
  empty_scope: {
    id: 'errors.empty_scope',
    defaultMessage: 'В охвате нет ни одного поля: проверьте размещение и землепользование.',
    description: 'Запуск кампании или назначение области, где по условиям не набралось ни одного поля',
  },
  exclusion_reason_required: {
    id: 'errors.exclusion_reason_required',
    defaultMessage: 'Укажите причину исключения хозяйства или поля из охвата.',
    description: 'Менеджер исключает хозяйство или поле из кампании без указания причины',
  },
  has_report: {
    id: 'errors.has_report',
    defaultMessage: 'По работе уже есть отчёт, поэтому её нельзя отменить.',
    description: 'Менеджер отменяет работу плана, по которой уже внесён отчёт',
  },
  not_cycle_work: {
    id: 'errors.not_cycle_work',
    defaultMessage: 'Эта работа не относится к агроциклу.',
    description: 'Запуск кампании по работе, которая не входит ни в один агроцикл',
  },
  not_plan_campaign: {
    id: 'errors.not_plan_campaign',
    defaultMessage: 'Эта кампания не относится к плану агроцикла.',
    description: 'Решение по кампании, которая создана не из плана агроцикла',
  },
  not_plan_work: {
    id: 'errors.not_plan_work',
    defaultMessage: 'Запустить можно только работу плана агроцикла.',
    description: 'Менеджер запускает кампанию по работе, которой нет в плане агроцикла',
  },
  nothing_to_extend: {
    id: 'errors.nothing_to_extend',
    defaultMessage: 'Новых полей для расширения охвата нет.',
    description: 'Менеджер расширяет охват кампании, но все подходящие поля уже в ней',
  },
  reason_required: {
    id: 'errors.reason_required',
    defaultMessage: 'Укажите причину: отмена и перенос работы без неё невозможны.',
    description: 'Менеджер отменяет или переносит работу плана, не заполнив причину',
  },
  window_confirm_required: {
    id: 'errors.window_confirm_required',
    defaultMessage: 'Подтвердите изменение окна работы.',
    description: 'Менеджер меняет окно работы так, что требуется явное подтверждение',
  },
  window_incomplete: {
    id: 'errors.window_incomplete',
    defaultMessage: 'Окно работы задаётся обеими датами или не задаётся вовсе.',
    description: 'Менеджер указал только начало или только конец окна работы плана или хозяйства',
  },
  window_ordered: {
    id: 'errors.window_ordered',
    defaultMessage: 'Начало окна не может быть позже его конца.',
    description: 'Менеджер указал окно работы, где дата начала позже даты окончания',
  },
  work_cancelled: {
    id: 'errors.work_cancelled',
    defaultMessage: 'Работа плана отменена, и менять её уже нельзя.',
    description: 'Действие над работой плана агроцикла, которая снята из плана',
  },
  work_not_found: {
    id: 'errors.work_not_found',
    defaultMessage: 'Такой работы нет в плане этого агроцикла.',
    description: 'Действие над работой плана, которой нет в этом агроцикле',
  },
  area: {
    id: 'errors.area',
    defaultMessage: 'Площадь в отчёте не прошла проверку.',
    description: 'Проверка поля площади отчёта или обязательства отклонила значение',
  },
  campaign_kind: {
    id: 'errors.campaign_kind',
    defaultMessage: 'Кампания такого вида не принимает работы, внесённые вне среза.',
    description: 'Отчёт вносится по кампании, вид которой не допускает работ вне среза',
  },
  cycle_not_started: {
    id: 'errors.cycle_not_started',
    defaultMessage: 'Агроцикл ещё не начался.',
    description: 'Отчёт или запуск работы до начала агроцикла',
  },
  interval_order: {
    id: 'errors.interval_order',
    defaultMessage: 'Конец полива не может быть раньше его начала.',
    description: 'В отчёте о поливе дата окончания раньше даты начала; ошибка проверки интервала',
  },
  kind: {
    id: 'errors.kind',
    defaultMessage: 'Вид работы в отчёте не прошёл проверку.',
    description: 'Проверка поля вида работы отчёта отклонила значение',
  },
  no_irrigation_work: {
    id: 'errors.no_irrigation_work',
    defaultMessage: 'В плане агроцикла нет поливов, поэтому вид полива неизвестен.',
    description: 'Вносится полив вне плана для агроцикла, в плане которого нет ни одной работы-полива',
  },
  not_current: {
    id: 'errors.not_current',
    defaultMessage: 'Отчёт уже исправлен или отозван.',
    description: 'Менеджер отзывает или правит версию отчёта, которая уже не действующая',
  },
  not_derived: {
    id: 'errors.not_derived',
    defaultMessage: 'Отозвать можно только отчёт по поливу, созданный из файла ирригации.',
    description: 'Менеджер отзывает отчёт, который не получен автоматически из файла ирригации',
  },
  obligation_cancelled: {
    id: 'errors.obligation_cancelled',
    defaultMessage: 'Работа плана отменена, отчёт по ней не принимается.',
    description: 'Отчёт вносится по работе плана, которая отменена',
  },
  obligation_has_report: {
    id: 'errors.obligation_has_report',
    defaultMessage: 'По работе плана уже есть отчёт.',
    description: 'Действие над работой плана, у которой уже есть отчёт',
  },
  prescription_irrigation: {
    id: 'errors.prescription_irrigation',
    defaultMessage: 'Предписание не может быть поливом: полив отмечает только файл ирригации.',
    description: 'Менеджер создаёт предписание категории полив',
  },
  receiver_key_missing: {
    id: 'errors.receiver_key_missing',
    defaultMessage: 'Для приёмника без агроцикла нужно указать год.',
    description: 'Отчёт направлен приёмнику без агроцикла, и в нём не указан год',
  },
  source_ref: {
    id: 'errors.source_ref',
    defaultMessage: 'Ссылка на источник отчёта не прошла проверку.',
    description: 'Проверка поля source_ref отчёта или обязательства отклонила значение',
  },
  supersedes_not_current: {
    id: 'errors.supersedes_not_current',
    defaultMessage: 'Заменяемая версия отчёта уже исправлена.',
    description: 'Исправление отчёта указывает на версию, которая перестала быть действующей',
  },
  supersedes_other_obligation: {
    id: 'errors.supersedes_other_obligation',
    defaultMessage: 'Заменяемая версия отчёта относится к другой работе плана.',
    description: 'Исправление отчёта указывает на версию, принадлежащую другой работе плана',
  },
  code_taken: {
    id: 'errors.code_taken',
    defaultMessage: 'Вид работы с таким кодом уже есть.',
    description: 'Автор шаблона создаёт вид работы с кодом, который уже занят',
  },
  if_match_required: {
    id: 'errors.if_match_required',
    defaultMessage: 'Изменение шаблона требует ревизии, которую вы видели.',
    description: 'Клиент отправил запись шаблона без заголовка If-Match',
  },
  invalid: {
    id: 'errors.invalid',
    defaultMessage: 'Данные не прошли проверку.',
    description: 'Конструктор шаблона отклонил введённые данные как неверные',
  },
  invalid_timing: {
    id: 'errors.invalid_timing',
    defaultMessage: 'Срок задан неверно: день указывается как «ММ-ДД» и должен существовать в календаре.',
    description: 'Автор шаблона вводит день срока этапа или работы в неверном формате или несуществующую дату',
  },
  stage_not_empty: {
    id: 'errors.stage_not_empty',
    defaultMessage: 'В этапе есть работы: перенесите или снимите их.',
    description: 'Автор шаблона или менеджер удаляет этап, в котором ещё есть работы',
  },
  template_changed: {
    id: 'errors.template_changed',
    defaultMessage: 'Шаблон изменился после открытия: обновите экран.',
    description: 'Автор шаблона сохраняет изменения, а другой человек успел изменить шаблон раньше',
  },
  template_exists: {
    id: 'errors.template_exists',
    defaultMessage: 'Шаблон для этой культуры уже есть.',
    description: 'Автор шаблона создаёт шаблон, который уже существует',
  },
  work_is_anchor: {
    id: 'errors.work_is_anchor',
    defaultMessage: 'Эта работа опорная: снять или изменить её нельзя.',
    description: 'Автор шаблона или менеджер пытается снять опорную работу, от которой зависят сроки других',
  },
  canon_version_unknown: {
    id: 'errors.canon_version_unknown',
    defaultMessage: 'Версия формата файла воды не поддерживается.',
    description: 'Пользователь загружает файл воды с версией канона, которую система не знает',
  },
  columns_missing: {
    id: 'errors.columns_missing',
    defaultMessage: 'В файле нет обязательных колонок.',
    description: 'Пользователь загружает файл воды в CSV без обязательных колонок',
  },
  header_inconsistent: {
    id: 'errors.header_inconsistent',
    defaultMessage: 'Организация и период должны быть одинаковы во всех строках файла.',
    description: 'В файле воды заголовок отчёта различается между строками',
  },
  issuer_missing: {
    id: 'errors.issuer_missing',
    defaultMessage: 'В файле не указана организация, выдавшая отчёт.',
    description: 'В файле воды не заполнено поле issuer',
  },
  json_invalid: {
    id: 'errors.json_invalid',
    defaultMessage: 'Файл не удалось прочитать как JSON.',
    description: 'Пользователь загружает файл воды с повреждённым JSON',
  },
  link_not_active: {
    id: 'errors.link_not_active',
    defaultMessage: 'Переназначить можно только действующую связь события.',
    description: 'Менеджер переназначает событие полива, чья связь с работой уже не действует',
  },
  note_required: {
    id: 'errors.note_required',
    defaultMessage: 'Добавьте пояснение: без него спор или его разбор невозможны.',
    description: 'Менеджер оспаривает событие полива или закрывает спор без пояснения',
  },
  period_empty: {
    id: 'errors.period_empty',
    defaultMessage: 'Период отчёта пуст: начало позже конца.',
    description: 'В файле воды дата начала периода позже даты окончания',
  },
  period_missing: {
    id: 'errors.period_missing',
    defaultMessage: 'Период отчёта не указан или не распознан.',
    description: 'В файле воды не задан период или его даты нельзя прочитать',
  },
  row_no_duplicate: {
    id: 'errors.row_no_duplicate',
    defaultMessage: 'Номер строки встречается в файле дважды.',
    description: 'В файле воды два одинаковых значения row_no',
  },
  row_no_invalid: {
    id: 'errors.row_no_invalid',
    defaultMessage: 'Номер строки должен быть целым числом не меньше 1.',
    description: 'В файле воды значение row_no не целое или меньше 1',
  },
  rows_missing: {
    id: 'errors.rows_missing',
    defaultMessage: 'В отчёте нет ни одной строки.',
    description: 'Пользователь загружает файл воды без строк данных',
  },
  same_obligation: {
    id: 'errors.same_obligation',
    defaultMessage: 'Событие уже относится к этой работе плана.',
    description: 'Менеджер переназначает событие полива на работу, к которой оно уже привязано',
  },
  event_withdrawn: {
    id: 'errors.event_withdrawn',
    defaultMessage: 'Событие снято новой версией файла.',
    description: 'Менеджер разбирает событие полива, которое отозвано загрузкой новой версии файла',
  },
  link_ambiguous: {
    id: 'errors.link_ambiguous',
    defaultMessage: 'У события несколько действующих связей или ни одной: однозначно переназначить нечего.',
    description: 'Менеджер переназначает событие полива с неоднозначной связью',
  },
  not_in_review: {
    id: 'errors.not_in_review',
    defaultMessage: 'Разбирать можно только событие в состоянии «нужен разбор».',
    description: 'Менеджер разбирает событие полива, которому разбор не требуется',
  },
  wrong_contour: {
    id: 'errors.wrong_contour',
    defaultMessage: 'Работа относится к другому контуру.',
    description: 'Менеджер привязывает событие полива к работе плана другого контура',
  },
  wrong_cycle: {
    id: 'errors.wrong_cycle',
    defaultMessage: 'Работа относится к другому агроциклу.',
    description: 'Менеджер привязывает событие полива к работе плана другого агроцикла',
  },
  comment_required: {
    id: 'errors.comment_required',
    defaultMessage: 'Напишите комментарий: отчёт и проверка записываются словами.',
    description: 'Исполнитель или проверяющий выполняет действие по предписанию или ситуации без комментария',
  },
  due_before_cut: {
    id: 'errors.due_before_cut',
    defaultMessage: 'Срок не может быть раньше даты среза постановки.',
    description: 'Менеджер ставит предписание со сроком раньше среза данных',
  },
  grounds_required: {
    id: 'errors.grounds_required',
    defaultMessage: 'Укажите основания: без них действие невозможно.',
    description: 'Менеджер создаёт предписание или отмечает ситуацию без оснований',
  },
  invalid_transition: {
    id: 'errors.invalid_transition',
    defaultMessage: 'Из текущего состояния так перейти нельзя.',
    description: 'Действие над предписанием или ситуацией, которое не предусмотрено для их текущего состояния',
  },
  not_noticed: {
    id: 'errors.not_noticed',
    defaultMessage: 'По этой ситуации ещё нет решения менеджера.',
    description: 'Действие над ситуацией, которую менеджер ещё не заметил',
  },
  unknown_actor: {
    id: 'errors.unknown_actor',
    defaultMessage: 'Хозяйства из ключа не существует.',
    description: 'Ключ предписания или ситуации указывает на несуществующее хозяйство',
  },
  unknown_campaign: {
    id: 'errors.unknown_campaign',
    defaultMessage: 'Кампании из ключа не существует.',
    description: 'Ключ предписания или ситуации указывает на несуществующую кампанию',
  },
  invalid_report: {
    id: 'errors.invalid_report',
    defaultMessage: 'Это не отчёт режима «Отчёт».',
    description: 'В аналитику передан объект, который не является отчётом ИИ-аналитика',
  },
  report_invalid: {
    id: 'errors.report_invalid',
    defaultMessage: 'Отчёт повреждён и не может быть показан.',
    description: 'Отчёт ИИ-аналитика не прошёл проверку структуры при обновлении',
  },
  unsigned_sql: {
    id: 'errors.unsigned_sql',
    defaultMessage: 'Отчёт не обновлён: в нём запросы, которых сервер не собирал. Попросите ассистента собрать отчёт заново.',
    description: 'Отчёт ИИ-аналитика изменён или перенесён из старой версии, и подпись его запросов к базе не сходится',
  },
});

const UNKNOWN = defineMessages({
  unknown: {
    id: 'errors.unknown',
    defaultMessage: 'Сервер отказал (код {code}).',
    description: 'Ошибка с кодом, которого нет в каталоге; {code} — машинный код для поддержки',
  },
});

export function hasErrorMessage(code: string): code is keyof typeof API_ERROR_MESSAGES {
  return Object.prototype.hasOwnProperty.call(API_ERROR_MESSAGES, code);
}

/** Текст ошибки API на языке интерфейса. */
export function describeApiError(intl: IntlShape, error: { code?: string | null; message?: string | null }): string {
  const code = error.code ?? '';
  const serverText = error.message?.trim() || null;
  if (intl.locale === SOURCE_LOCALE && serverText) return serverText;
  if (code && hasErrorMessage(code)) return intl.formatMessage(API_ERROR_MESSAGES[code]);
  if (code) return intl.formatMessage(UNKNOWN.unknown, { code });
  return serverText ?? intl.formatMessage(API_ERROR_MESSAGES.internal_error);
}
